import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { ButtonContainer } from '../ButtonContainer/ButtonContainer'
import { Button } from '../Button/Button'
import deprecated from '../../utils/deprecated'

/**
 * Bronson Card component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-cards.html
 *
 * @param {React.ReactNode} [additionalInfo] - Additional info content.
 * @param {Button|Button[]} [buttons] - A collection of {@link Button} components.
 * @param {React.ReactNode} [children] - The Card body content.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {boolean} [collapsible] - Deprecated: Render the Card as a collapsible variant.
 * @param {React.ReactNode} [contentLarge] - Additional content for the {@link expandable} variant.
 * @param {React.ReactNode} [contentShort] - Additional brief content to display below the {@link title} for non-offer variants.
 * @param {boolean} [defaultExpanded] - Whether the Card should be expanded or not. Only applicable if {@link expandable} is `true`.
 * @param {boolean} [disabled] - Disable the Card.
 * @param {string} [element] - The Card element itself, e.g. 'article'. Please use a semantic block-level element.
 * @param {boolean} [error] - Render the Card as error variant.
 * @param {boolean} [expandable] - Render the Card as an expandable variant.
 * @param {string} [expandableTestId] - The `[data-testid]` attribute on the expandable button element.
 * @param {boolean} [footer] - Display the Card footer element.
 * @param {React.ReactNode} [footerContent] - Additional footer content.
 * @param {string} [icon] - The Card icon to use, e.g. 'semantic-clock'.
 * @param {IconList} [iconList] - Display an {@link IconList}.
 * @param {string} [imageAlt] - The Card image alt text. Takes precedence over {@link imageProps.alt}.
 * @param {object} [imageProps] - An object of image props.
 * @param {string} [imageSrc] - The Card image source. Takes precedence over {@link imageProps.src}.
 * @param {boolean} [inactive] - Render the Card as inactive variant.
 * @param {React.ReactNode} [mediaElement] - Media element to use with Google Maps.
 * @param {boolean} [offer] - Render the Card as offer variant.
 * @param {boolean} [scrollable] - Make the Card scrollable.
 * @param {boolean} [small] - Render the Card as small variant.
 * @param {React.ReactNode} [subtitle] - Additional subtitle element.
 * @param {boolean} [success] - Render the Card as success variant.
 * @param {Tag} [tags] - Display a {@link Tag}.
 * @param {React.ReactNode} [title] - The title element.
 * @param {boolean} [warning] - Render the Card as warning variant.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Card component.
 * @constructor
 */
export function Card({
  additionalInfo,
  buttons,
  children,
  className,
  collapsible,
  contentLarge,
  contentShort,
  defaultExpanded,
  disabled,
  element,
  error,
  expandable,
  expandableTestId,
  footer,
  footerContent,
  icon,
  iconList,
  imageAlt,
  imageProps,
  imageSrc,
  inactive,
  mediaElement,
  offer,
  scrollable,
  small,
  subtitle,
  success,
  tags,
  title,
  warning,
  testId,
  ...otherProps
}) {
  /**
   * Track the expanded state of the {@link Card}.
   */
  const [expanded, setExpanded] = useState(defaultExpanded)

  const CustomTagCardElement = `${element}`

  const customTagClassNameList = classNames(
    'c-card',
    {
      'c-card--image': (imageSrc || imageProps) && !offer,
      'c-card--button-group': buttons && buttons.length > 0 && !offer,
      'c-card--states': success || warning || error,
      'c-card--states-success': success,
      'c-card--states-warning': warning,
      'c-card--states-error': error,
      'c-card--inactive': inactive,
      'c-card--scrollable': scrollable,
      'c-card--collapsible': collapsible,
      'c-card--expandable': expandable,
      'c-card--small': small,
      'c-card--offer': offer,
    },
    className
  ).trim()

  const cardIconClassNameList = classNames('c-icon', {
    [`c-icon--[${icon}]`]: true,
  }).trim()

  const expandableClassNameList = classNames('c-card__expandable', 'js-card-expandable', 'js-is-fx', 'js-is-ready', {
    'is-open': expanded,
  }).trim()

  const expandablePanelClassNameList = classNames(
    'c-card__expandable-panel',
    'js-card-expandable__panel',
    'js-is-fx',
    'js-is-ready',
    {
      'is-closed': !expanded,
    }
  ).trim()

  const expandablePanelStyle = expanded
    ? {}
    : {
        height: '0px',
        overflowY: 'hidden',
        visibility: 'hidden',
      }

  function renderIfIcon() {
    if (icon) {
      return (
        <div className="c-card__icon">
          <i className={cardIconClassNameList} aria-hidden="true" role="img" />
        </div>
      )
    }
    return null
  }

  function renderIfImage() {
    if (imageSrc || imageProps) {
      return (
        <div className="c-card__image">
          <img src={imageSrc} alt={imageAlt} {...imageProps} />
          {renderBadge()}
        </div>
      )
    }
    return null
  }

  const renderIfMedia = () => {
    if (mediaElement) {
      return <div className="c-card__image">{mediaElement}</div>
    }
    return null
  }

  function renderIfSubtitle() {
    if (subtitle) {
      return <p className="c-card__subtitle">{subtitle}</p>
    }
    return null
  }

  function renderIfTitle() {
    if (title) {
      return (
        <header className="c-card__header">
          <h4 className="c-card__title">{title}</h4>
          {renderIfSubtitle()}
        </header>
      )
    }
    return null
  }

  function renderIfOffer() {
    if (offer) {
      return <>{renderIfTitle()}</>
    }
    return null
  }

  function renderBadge() {
    if (offer) {
      return (
        <div className="c-card__badge">
          <ul className="c-tag-list">
            <li>{tags}</li>
          </ul>
        </div>
      )
    }
    return null
  }

  function renderIfTitleUnlessOffer() {
    if (title) {
      return (
        <header className="c-card__header">
          {expandable ? (
            <>
              <div className="c-card__header-items">
                <h4 className="c-card__title">{title}</h4>
                {contentShort}
              </div>
            </>
          ) : (
            <>
              <h4 className="c-card__title">{title}</h4>
            </>
          )}
          {renderIfSubtitle()}
        </header>
      )
    }
    return null
  }

  function renderUnlessOffer() {
    if (!offer) {
      return <>{renderIfTitleUnlessOffer()}</>
    }
    return null
  }

  function renderIfOfferBody() {
    if (offer) {
      return (
        <>
          <div className="c-card__icon-list">{iconList}</div>
          <hr className="c-card__divider" />
        </>
      )
    }
    return null
  }

  function renderIfButtons() {
    if (buttons) {
      return Array.isArray(buttons) ? <ButtonContainer center>{buttons}</ButtonContainer> : buttons
    }
    return null
  }

  function renderIfContentLarge() {
    if (contentLarge) {
      return <>{contentLarge}</>
    }
    return null
  }

  function renderIfIsExpandable() {
    if (expandable) {
      return (
        <>
          <div className={expandableClassNameList} aria-disabled={disabled && 'true'}>
            <Button
              aria-expanded={expanded ? 'true' : 'false'}
              className="c-btn--card c-card__expandable-btn js-card-expandable__trigger"
              data-testid={expandableTestId}
              icon="semantic-expand"
              iconClassName="c-btn--card-icon"
              type="button"
              onClick={() => {
                setExpanded(!expanded)
              }}
            />
            {/**
             * Need to pass `[inert=""]` as React currently does not support HTMLElement.inert mapping.
             * @see https://github.com/facebook/react/pull/24730
             * @TODO: Remove once React supports direct `[inert]` usage.
             */}
            <div
              className={expandablePanelClassNameList}
              style={expandablePanelStyle}
              aria-hidden={expanded ? 'false' : 'true'}
              inert={expanded ? null : ''}
            >
              {renderIfContentLarge()}
            </div>
          </div>
        </>
      )
    }
    return null
  }

  function renderIfAdditionalInfo() {
    if (additionalInfo) {
      return <div className="u-text-center">{additionalInfo}</div>
    }
    return null
  }

  function renderIfOfferButtons() {
    if (offer) {
      return (
        <ButtonContainer center stretch vertical>
          {buttons}
        </ButtonContainer>
      )
    }
    return null
  }

  function renderIfOfferFooterDivider() {
    if (offer) {
      return <hr className="c-card__divider" />
    }
    return null
  }

  function renderIfFooter() {
    if (footer) {
      return (
        <footer className="c-card__footer">
          {renderIfOfferFooterDivider()}
          {!offer && renderIfButtons()}
          {footerContent}
        </footer>
      )
    }
    return null
  }

  return (
    <CustomTagCardElement {...otherProps} data-testid={testId} className={customTagClassNameList}>
      {renderIfIcon()}
      {renderIfOffer()}
      {renderIfImage()}
      {renderIfMedia()}
      {renderUnlessOffer()}
      <div className="c-card__body">
        {renderIfOfferBody()}
        {children}
        {renderIfIsExpandable()}
      </div>
      {renderIfAdditionalInfo()}
      {renderIfOfferButtons()}
      {renderIfFooter()}
    </CustomTagCardElement>
  )
}

Card.propTypes = {
  additionalInfo: PropTypes.node, // Bronson template: 'card-additional-info'.
  buttons: PropTypes.oneOfType([PropTypes.node, PropTypes.array]), // Bronson template: 'card-buttons'. Use {@link Button} component.
  children: PropTypes.node, // Bronson template: 'card-body'.
  className: PropTypes.string, // Bronson template: 'card-modifier'.
  contentLarge: PropTypes.node, // Bronson template: 'card-content-large'.
  contentShort: PropTypes.node, // Bronson template: 'card-content-short'.
  defaultExpanded: PropTypes.bool,
  disabled: PropTypes.bool, // Bronson template: 'disabled'.
  element: PropTypes.string, // Bronson template: 'card-element'.
  expandableTestId: PropTypes.string, // Added for `[data-testid]` attribute on the expandable button.
  footer: PropTypes.bool, // Bronson template: 'card-footer'.
  footerContent: PropTypes.node, // Bronson template: 'card-footer-text'.
  icon: PropTypes.string, // Bronson template: 'card-icon'.
  iconList: PropTypes.node, // Bronson template: '@bronson-icon-list'. Use {@link IconList} component.
  imageAlt: PropTypes.string,
  imageProps: PropTypes.oneOfType([PropTypes.object]),
  imageSrc: PropTypes.string, // Bronson template: 'card-image'.
  mediaElement: PropTypes.node, // Use it with media element, e.g. Google Maps.
  subtitle: PropTypes.node, // Bronson template: 'card-subtitle'.
  tags: PropTypes.node, // Bronson template: '@bronson-tag'. Use {@link Tag} component.
  title: PropTypes.node, // Bronson template: 'card-title'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /**
   * Convenience props.
   */
  error: PropTypes.bool, // Convenience prop for `.c-card--states-error`. Bronson template: 'card-modifier'.
  expandable: PropTypes.bool, // Convenience prop for `.c-card--expandable`. Bronson template: 'card-modifier'.
  inactive: PropTypes.bool, // Convenience prop for `.c-card--inactive`. Bronson template: 'card-modifier'.
  offer: PropTypes.bool, // Convenience prop for `.c-card--offer`. Bronson template: 'card-modifier'.
  scrollable: PropTypes.bool, // Convenience prop for `.c-card--scrollable`. Bronson template: 'card-modifier'.
  small: PropTypes.bool, // Convenience prop for `.c-card--small`. Bronson template: 'card-modifier'.
  success: PropTypes.bool, // Convenience prop for `.c-card--states-success`. Bronson template: 'card-modifier'.
  warning: PropTypes.bool, // Convenience prop for `.c-card--states-warning`. Bronson template: 'card-modifier'.
  /**
   * Deprecated props.
   */
  collapsible: deprecated(
    PropTypes.bool,
    'Deprecated, this modifier is no longer necessary. Will be removed in Bronson-React v6.0.0.'
  ), // Convenience prop for `.c-card--collapsible`. Bronson template: 'card-modifier'.
}
